/**
 * Ticker Component
 *
 * Minimal horizontal scrolling announcement ticker.
 * Features:
 * - Seamless infinite scroll (no reset)
 * - Drag to scroll with mouse
 * - Click to open external URL
 * - Color-coded by type with org logos
 */

import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useAnnouncements, useOrganizations } from '../../hooks';
import { COLORS } from '../../lib/constants';
import type { TickerType } from '../../types';

// Background colors for each announcement type
const TYPE_COLORS: Record<TickerType, string> = {
  vacancy: COLORS.cyan,
  open_call: COLORS.springGreen,
  subsidy: COLORS.lime,
  other: COLORS.mauve,
};

const SCROLL_SPEED = 50; // pixels per second
const ITEM_GAP = 10; // gap between items (same as vertical padding)

export function Ticker() {
  const { announcements, loading } = useAnnouncements();
  const { organizations } = useOrganizations();
  const [isDragging, setIsDragging] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragStartScroll, setDragStartScroll] = useState(0);
  const contentRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number | undefined>(undefined);
  const lastTimeRef = useRef<number>(0);
  const dragMovedRef = useRef(false);
  const contentWidthRef = useRef(0);

  // Build logo lookup map from organizations
  const logoMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const org of organizations) {
      if (org.logo_url) {
        map.set(org.id, org.logo_url);
      }
    }
    return map;
  }, [organizations]);

  // Measure content width
  useEffect(() => {
    if (contentRef.current && announcements.length > 0) {
      const items = contentRef.current.children;
      let width = 0;
      const oneSetLength = Math.floor(items.length / 3);
      for (let i = 0; i < oneSetLength; i++) {
        width += (items[i] as HTMLElement).offsetWidth + ITEM_GAP;
      }
      contentWidthRef.current = width;
    }
  }, [announcements]);

  // Animation loop
  useEffect(() => {
    if (loading || announcements.length === 0 || isDragging) {
      return;
    }

    const animate = (time: number) => {
      if (lastTimeRef.current === 0) {
        lastTimeRef.current = time;
      }

      const delta = time - lastTimeRef.current;
      lastTimeRef.current = time;

      setScrollPosition((prev) => {
        const newPos = prev - (SCROLL_SPEED * delta) / 1000;
        if (contentWidthRef.current > 0 && Math.abs(newPos) >= contentWidthRef.current) {
          return newPos + contentWidthRef.current;
        }
        return newPos;
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      lastTimeRef.current = 0;
    };
  }, [loading, announcements.length, isDragging]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    setIsDragging(true);
    setDragStartX(e.clientX);
    setDragStartScroll(scrollPosition);
    dragMovedRef.current = false;
  }, [scrollPosition]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging) return;
    const delta = e.clientX - dragStartX;
    if (Math.abs(delta) > 5) {
      dragMovedRef.current = true;
    }
    setScrollPosition(dragStartScroll + delta);
  }, [isDragging, dragStartX, dragStartScroll]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleClick = useCallback((url: string) => {
    if (dragMovedRef.current) {
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  }, []);

  if (loading || !announcements || announcements.length === 0) {
    return null;
  }

  // Triple the announcements for seamless looping
  const tripleAnnouncements = [...announcements, ...announcements, ...announcements];

  return (
    <div
      style={{
        overflow: 'hidden',
        position: 'relative',
        cursor: isDragging ? 'grabbing' : 'grab',
        userSelect: 'none',
        padding: `${ITEM_GAP}px 0`,
      }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
      data-testid="ticker"
    >
      <div
        ref={contentRef}
        style={{
          display: 'flex',
          gap: `${ITEM_GAP}px`,
          whiteSpace: 'nowrap',
          transform: `translateX(${scrollPosition}px)`,
          willChange: 'transform',
        }}
      >
        {tripleAnnouncements.map((announcement, index) => (
          <div
            key={`${announcement.id}-${index}`}
            onClick={() => handleClick(announcement.externalUrl)}
            role="link"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleClick(announcement.externalUrl);
              }
            }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '10px',
              padding: `${ITEM_GAP}px ${ITEM_GAP * 2}px ${ITEM_GAP}px ${ITEM_GAP}px`,
              cursor: 'pointer',
              color: COLORS.graphite,
              fontSize: '16px',
              backgroundColor: TYPE_COLORS[announcement.type],
              flexShrink: 0,
            }}
            data-testid="ticker-item"
          >
            {logoMap.get(announcement.organizationId) && (
              <img
                src={logoMap.get(announcement.organizationId)}
                alt=""
                style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  backgroundColor: COLORS.parchment,
                  objectFit: 'cover',
                  flexShrink: 0,
                  border: `1px solid ${COLORS.graphite}20`,
                }}
                onError={(e) => {
                  // Hide image if it fails to load
                  e.currentTarget.style.display = 'none';
                }}
              />
            )}
            <span>{announcement.headline}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
